import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import {
  startDeleteFlowRoleCiiuAssignment,
  startLoadingFlowRoleCiiuAssignments,
  startRegisterFlowRoleCiiuAssignment
} from "../../app/actions/flowRoleCiiuAssignmentActions";
import { startLoadingCatalogRoles } from "../../app/actions/catalogRoleActions";
import { startLoadingCatalogCiiusByLevel1 } from "../../app/actions/catalogCiiuActions";
import { startLoadingUsers } from "../../app/actions/userActions";
import FlowEngineGlobalGate from "../specialist-flow-engine/FlowEngineGlobalGate";

const SPECIALIST_ROLES = [
  "Specialist-Manager",
  "Specialist-Validator",
  "Specialist-Editor"
];

const AdminFlowCiiuAssignmentScreen = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { assignments, loading } = useSelector((state) => state.flowRoleCiiuAssignment);
  const { catalogRoles = [] } = useSelector((state) => state.catalogRole);
  const { listCatalogCiiusLevel1 = [] } = useSelector((state) => state.catalogCiiu);
  const { users = [] } = useSelector((state) => state.user);

  const [form, setForm] = useState({
    user_id: "",
    catalogrole_id: "",
    catalogciiu_id: "",
    catalogmodule_id: "",
    observation: ""
  });

  useEffect(() => {
    dispatch(startLoadingFlowRoleCiiuAssignments());
    dispatch(startLoadingCatalogRoles());
    dispatch(startLoadingCatalogCiiusByLevel1(1));
    dispatch(startLoadingUsers());
  }, [dispatch]);

  const level1Ciius = useMemo(
    () => (listCatalogCiiusLevel1 || []).filter((item) => !item.deleted),
    [listCatalogCiiusLevel1]
  );

  const specialistRoles = useMemo(
    () => (catalogRoles || []).filter((role) => SPECIALIST_ROLES.includes(role.name)),
    [catalogRoles]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.user_id || !form.catalogrole_id || !form.catalogciiu_id) {
      Swal.fire(
        t("titles.error"),
        t("admin.flowCiiu.validation.required", "Usuario, rol y CIIU nivel 1 son obligatorios"),
        "error"
      );
      return;
    }

    const payload = {
      user_id: Number(form.user_id),
      catalogrole_id: Number(form.catalogrole_id),
      catalogciiu_id: Number(form.catalogciiu_id),
      catalogmodule_id: form.catalogmodule_id ? Number(form.catalogmodule_id) : null,
      observation: form.observation || ""
    };

    const created = await dispatch(startRegisterFlowRoleCiiuAssignment(payload));
    if (created) {
      Swal.fire(t("titles.success"), t("admin.flowCiiu.saved", "Parametrización guardada"), "success");
      setForm({
        user_id: "",
        catalogrole_id: "",
        catalogciiu_id: "",
        catalogmodule_id: "",
        observation: ""
      });
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: t("titles.confirm"),
      text: t("admin.flowCiiu.deleteConfirm", "¿Eliminar esta parametrización?"),
      icon: "warning",
      showCancelButton: true
    });

    if (confirm.isConfirmed) {
      await dispatch(startDeleteFlowRoleCiiuAssignment(id));
    }
  };

  return (
    <FlowEngineGlobalGate>
    <div className="container py-4">
      <h2>{t("admin.flowCiiu.title", "Parametrización CIIU — Rol — Usuario")}</h2>
      <p className="text-muted">
        {t(
          "admin.flowCiiu.subtitle",
          "Asigna CIIUs de nivel 1 a especialistas por rol. La bandeja consultiva usará esta configuración."
        )}
      </p>

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <form className="row g-3" onSubmit={handleSubmit}>
            <div className="col-md-3">
              <label className="form-label">{t("admin.flowCiiu.user", "Usuario")}</label>
              <select className="form-select" name="user_id" value={form.user_id} onChange={handleChange}>
                <option value="">{t("general.select", "Seleccionar")}</option>
                {(users || []).map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} {user.last_name} ({user.email})
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">{t("admin.flowCiiu.role", "Rol especialista")}</label>
              <select className="form-select" name="catalogrole_id" value={form.catalogrole_id} onChange={handleChange}>
                <option value="">{t("general.select", "Seleccionar")}</option>
                {specialistRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">{t("admin.flowCiiu.ciiu", "CIIU nivel 1")}</label>
              <select className="form-select" name="catalogciiu_id" value={form.catalogciiu_id} onChange={handleChange}>
                <option value="">{t("general.select", "Seleccionar")}</option>
                {level1Ciius.map((ciiu) => (
                  <option key={ciiu.id} value={ciiu.id}>
                    {ciiu.code} — {ciiu.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">{t("admin.flowCiiu.moduleOptional", "Módulo (opcional)")}</label>
              <input
                type="number"
                className="form-control"
                name="catalogmodule_id"
                value={form.catalogmodule_id}
                onChange={handleChange}
                placeholder={t("admin.flowCiiu.moduleHint", "Vacío = todos")}
              />
            </div>
            <div className="col-12">
              <label className="form-label">{t("admin.flowCiiu.observation", "Observación")}</label>
              <input
                type="text"
                className="form-control"
                name="observation"
                value={form.observation}
                onChange={handleChange}
              />
            </div>
            <div className="col-12">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {t("buttons.save", "Guardar")}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header">
          <h5 className="mb-0">{t("admin.flowCiiu.list", "Parametrizaciones activas")}</h5>
        </div>
        <div className="table-responsive">
          <table className="table table-striped mb-0">
            <thead>
              <tr>
                <th>ID</th>
                <th>{t("admin.flowCiiu.user", "Usuario")}</th>
                <th>{t("admin.flowCiiu.role", "Rol")}</th>
                <th>{t("admin.flowCiiu.ciiu", "CIIU")}</th>
                <th>{t("admin.flowCiiu.moduleOptional", "Módulo")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-4">{t("general.loading", "Cargando...")}</td>
                </tr>
              ) : assignments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-muted">
                    {t("admin.flowCiiu.empty", "Sin parametrizaciones")}
                  </td>
                </tr>
              ) : (
                assignments.map((row) => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>
                      {row.User_To_FlowRoleCiiuAssignment
                        ? `${row.User_To_FlowRoleCiiuAssignment.name} ${row.User_To_FlowRoleCiiuAssignment.last_name || ""}`
                        : row.user_id}
                    </td>
                    <td>{row.CatalogRole_To_FlowRoleCiiuAssignment?.name || row.catalogrole_id}</td>
                    <td>
                      {row.CatalogCiiu_To_FlowRoleCiiuAssignment
                        ? `${row.CatalogCiiu_To_FlowRoleCiiuAssignment.code} — ${row.CatalogCiiu_To_FlowRoleCiiuAssignment.name}`
                        : row.catalogciiu_id}
                    </td>
                    <td>{row.catalogmodule_id || t("admin.flowCiiu.allModules", "Todos")}</td>
                    <td>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row.id)}>
                        {t("buttons.delete", "Eliminar")}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </FlowEngineGlobalGate>
  );
};

export default AdminFlowCiiuAssignmentScreen;
